import { istKompakt } from '../../components/island/island-size';

/** Hülle jedes Dialogs: deckt den Schirm ab und zentriert die Karte.
 *  Trägt bewusst keine Polsterung — die Safe-Areas sind Sache der Karte
 *  (siehe `modalKarte`), weil die Hülle nicht weiß, ob ihre Karte gerade
 *  randlos Vollbild ist oder nicht: sie wird an allen elf Dialogen identisch
 *  eingesetzt, ohne `art` oder Viewport zu kennen. Ein Polster hier würde
 *  bei jeder Vollbild-Karte einen sichtbaren Rand erzwingen (der Bug, den
 *  dieser Kommentar-Nachbar behebt) — es lässt sich nicht ohne Kenntnis des
 *  Zweigs weglassen.
 *  Die Abdunklung und das z-index bleiben Sache des jeweiligen Dialogs —
 *  die Werte gehen von z-[50] bis z-[10000] und haben ihre Gründe. */
export const MODAL_HUELLE = 'fixed inset-0 flex items-center justify-center';

/** Die Karte im Dialog.
 *  `gross` = man erfasst oder bearbeitet etwas: auf schmalen ODER kurzen
 *  Viewports randlos Vollbild — ein 780-px-Formular hat auf 390 px keinen
 *  Platz, und im Querformat blieben von `max-h-[90vh]` nur 351 px.
 *  `klein` = eine Rückfrage: bleibt Karte, damit ein „Wirklich löschen?"
 *  nicht wie ein eigener Arbeitsschritt wirkt. Nur die Höhe wird gezähmt.
 *  `dvh` statt `vh`, weil `vh` auf iOS die eingeblendeten Leisten ignoriert.
 *
 *  Safe-Areas: beide Zweige respektieren `env(safe-area-inset-*)`, aber an
 *  unterschiedlicher Stelle, weil sie unterschiedliche Dinge sein wollen.
 *  Vollbild soll wie der E-Mail-Verfassen-Editor aussehen: eine weiße Fläche,
 *  die bis UNTER die Notch reicht (kein Rand, kein Radius) — nur der Inhalt
 *  rückt per Innenpolster (`p*-[env(...)]`) vor der Notch zurück. Die Karte
 *  dagegen soll NEBEN der Notch liegen, nicht darunter — dafür braucht sie
 *  einen Außenabstand (`m*-[max(1rem,env(...))]`), der auch ohne Notch nie
 *  unter 1rem fällt. `w-full` statt einer Prozentbreite: mit vier eigenen
 *  Außenabständen bemisst sich die Breite bereits aus dem Freiraum, den die
 *  Hülle (flex, zentriert) übrig lässt — eine zusätzliche `w-[95%]` würde
 *  addiert und könnte in der Summe über den Schirmrand hinauslaufen. Die
 *  eigentliche Breite begrenzt weiterhin `maxBreite` vom Aufrufer.
 *  Vor diesem Fix lag ein Mindestpolster auf der Hülle, gemeinsam für beide
 *  Zweige — das erzwang im Vollbild-Zweig einen Rand (der randlos sein
 *  sollte) und kannte im Karten-Zweig keine Notch-Aussparung seitlich. Jetzt
 *  setzt jeder Zweig nur noch, was er selbst braucht, an seinem eigenen
 *  Element (Innen- vs. Außenabstand), und keine zwei Klassen konkurrieren
 *  mehr um dieselbe Eigenschaft.
 *
 *  `radius` ist ein Parameter statt einer vom Aufrufer angehängten Klasse:
 *  zwei Klassen auf derselben CSS-Eigenschaft konkurrieren um dieselbe
 *  Stylesheet-Position — der Radius im Vollbild-Zweig muss ganz entfallen
 *  (randlos), was eine angehängte Klasse nicht leisten kann. Die Funktion
 *  kennt ihren Zweig bereits, also entscheidet sie: im Karten-Zweig gesetzt,
 *  im Vollbild-Zweig ignoriert. */
export function modalKarte(
  art: 'gross' | 'klein',
  maxBreite: string,
  viewport: { width: number; height: number },
  radius = 'rounded-2xl',
): string {
  const kompakt = istKompakt(viewport.width, viewport.height);
  if (art === 'gross' && kompakt) {
    return 'relative bg-white w-full h-full flex flex-col overflow-hidden '
      + 'pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] '
      + 'pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]';
  }
  return `relative bg-white ${radius} shadow-2xl w-full ${maxBreite} `
    + 'mt-[max(1rem,env(safe-area-inset-top))] mb-[max(1rem,env(safe-area-inset-bottom))] '
    + 'ml-[max(1rem,env(safe-area-inset-left))] mr-[max(1rem,env(safe-area-inset-right))] '
    + 'max-h-[calc(100dvh-2rem)] flex flex-col overflow-y-auto overflow-x-hidden';
}
